import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Stack,
  Switch,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import AnomalieFilters from "../Anomalie/AnomalieFilters";
import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Card from "./Card";
import { Info, TrendingUp } from "@mui/icons-material";
import SelectCountries from "../Select/SelectCountries";
import SelectAggregations from "../Select/SelectAggregations/SelectAggregations";
import SelectGeoUnit from "../Select/SelectGeoUnit";
import useQueryState from "../../hooks/useQueryState";
import { useMatch } from "react-router-dom";
import { ANOMALY_LAYERS } from "../../enums/layers";
import PeriodChart from "./partials/PeriodChart";
import GeographicalUnitPopUp from "../Select/SelectGeoUnit/partials/GeographicalUnitPopUp";
import useCountries from "../../hooks/useCountries";
import { INFO_CONTENT } from "../../enums/info";
import { useUIStore } from "../../store/ui";
import MaskingDialog from "./partials/Dialog";
import { isDetrendedAvailable } from "../../helpers/indicatorName";

const FiltersSidebar = () => {
  const [isOpenLandCoverMaskedDialog, setIsOpenLandCoverMaskedDialog] =
    useState(false);
  const [targetAreaAndPeriodExpanded, setTargetAreaAndPeriodExpanded] =
    useState(true);
  const [droughtEventsExpanded, setDroughtEventsExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<string>("");
  const {
    queryState: {
      season,
      droughtEvents,
      country,
      aggregationLevel,
      ipcScore,
      geographicalUnit,
      layers,
      payout: { comparisonFocus },
    },
    setQueryState,
  } = useQueryState();
  const {
    getCountryAdministrativeUnit: { data: geoUnit, isLoading },
  } = useCountries({ country, aggregationLevel });
  const [hoveredArea, setHoveredArea] = useState<string>(geographicalUnit);

  const handleTargetAreaAndPeriodChange = () => {
    setTargetAreaAndPeriodExpanded(!targetAreaAndPeriodExpanded);
  };

  const handleDroughtEventsChange = () => {
    setDroughtEventsExpanded(!droughtEventsExpanded);
  };

  const handleOpenDialog = (
    e: React.MouseEvent<HTMLButtonElement>,
    type: string,
  ) => {
    e.stopPropagation();
    setDialogContent(type);
  };

  const handleSeason = (
    e: ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event>,
    type: string,
  ) => {
    setQueryState({
      season: season.includes(type)
        ? season.filter((each) => each !== type)
        : [...season, type],
    });
  };

  const handleLandCoverMasked = () => {
    if (!localStorage.getItem("landCoverMaskedDilog")) {
      setIsOpenLandCoverMaskedDialog(true);
    }
    setLandCoverMasked();
  };

  const isDroughtRiskStep = useMatch("/dashboard/drought-risk");
  const isFinancialIndicesStep = useMatch("/dashboard/financial-indices");
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);
  const setDetrended = useUIStore((s) => s.setDetrended);
  const toggleDetrended = useUIStore((s) => s.toggleDetrended);
  const setLandCoverMasked = useUIStore((s) => s.toggleLandCoverMasked);
  const canUseDetrended = isDetrendedAvailable({ country, layers });

  useEffect(() => {
    if (!canUseDetrended && isDetrended) {
      setDetrended(false);
    }
  }, [canUseDetrended, isDetrended, setDetrended]);

  return (
    <Box
      borderRight="0.01rem solid #dadada"
      sx={{
        overflow: "auto",
        overflowY: "auto",
        overflowX: "hidden",
        height: "100%",
        maxWidth: "320px",
        minWidth: "320px",
      }}
    >
      <AccordionStyled
        expanded={targetAreaAndPeriodExpanded}
        onChange={handleTargetAreaAndPeriodChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={700}>
            Target area
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ paddingTop: 0, position: "relative" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SelectCountries />
            <SelectAggregations />
            <SelectGeoUnit
              open={open}
              setOpen={setOpen}
              hoveredArea={hoveredArea}
              setHoveredArea={setHoveredArea}
            />
          </Box>
        </AccordionDetails>
      </AccordionStyled>

      <Stack
        direction="row"
        alignItems="center"
        pl={2}
        pr={5}
        py={1}
        borderTop="1px solid #DBE4E9"
        borderRight="1px solid #DBE4E9"
        borderLeft="1px solid #DBE4E9"
      >
        <Stack direction="row" alignItems="center" flex={1} gap={0.5}>
          <Typography variant="subtitle1" fontWeight={700}>
            Land Cover Mask
          </Typography>
          <Tooltip title={INFO_CONTENT.landCoverMask.description}>
            <Info sx={{ color: "#6C6C89" }} fontSize="small" />
          </Tooltip>
        </Stack>
        <Box>
          <Switch
            checked={isLandCoverMasked}
            onChange={(e) => {
              handleLandCoverMasked();
            }}
            onClick={(e) => e.stopPropagation()}
            // disabled={Boolean(comparisonFocus === 0 && isFinancialIndicesStep)}
          />
        </Box>
      </Stack>

      {canUseDetrended && (
        <Box px={2} py={1} bgcolor="#F7F7F7">
          <Stack
            direction="row"
            alignItems="center"
            px={1.5}
            py={1}
            sx={{
              border: "1px solid #DBE4E9",
              borderRadius: "4px",
              backgroundColor: "#F1F3F4",
            }}
          >
            <Stack direction="row" alignItems="center" flex={1} gap={0.75}>
              <TrendingUp sx={{ color: "#6C6C89" }} fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                Detrended data
              </Typography>
              <Tooltip title={INFO_CONTENT.detrendedData.description}>
                <Info sx={{ color: "#6C6C89" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Box>
              <Switch
                checked={isDetrended}
                onChange={() => {
                  toggleDetrended();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          </Stack>
        </Box>
      )}

      {/* Season 1 */}
      <AccordionStyled
        expanded={season.includes("1")}
        onChange={(e) => handleSeason(e, "1")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" width="100%">
            <Stack direction="row" alignItems="center" flex={1} gap={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Season 1
              </Typography>
              <Tooltip title={INFO_CONTENT.season1.description}>
                <Info sx={{ color: "#6C6C89" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Box>
              <Switch
                checked={season.includes("1")}
                onChange={(e) => {
                  handleSeason(e, "1");
                }}
                onClick={(e) => e.stopPropagation()}
                // disabled={Boolean(comparisonFocus === 0 && isFinancialIndicesStep)}
              />
            </Box>
          </Stack>
        </AccordionSummary>
        <Box px={2} pb={2}>
          <PeriodChart season="1" />
        </Box>
        <Card
          layers={ANOMALY_LAYERS}
          season="1"
          openDialog={handleOpenDialog}
        />
      </AccordionStyled>

      {/* Season 2 */}
      <AccordionStyled
        expanded={season.includes("2")}
        onChange={(e) => handleSeason(e, "2")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" width="100%">
            <Stack direction="row" alignItems="center" flex={1} gap={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Season 2
              </Typography>
              <Tooltip title={INFO_CONTENT.season2.description}>
                <Info fontSize="small" sx={{ color: "#6C6C89" }} />
              </Tooltip>
            </Stack>
            <Box>
              <Switch
                checked={season.includes("2")}
                onChange={(e) => {
                  handleSeason(e, "2");
                }}
                onClick={(e) => e.stopPropagation()}
                // disabled={Boolean(comparisonFocus === 0 && isFinancialIndicesStep)}
              />
            </Box>
          </Stack>
        </AccordionSummary>
        <Box px={2} pb={2}>
          <PeriodChart season="2" />
        </Box>
        <Card
          layers={ANOMALY_LAYERS}
          season="2"
          openDialog={handleOpenDialog}
        />
      </AccordionStyled>

      {!isFinancialIndicesStep && (
        <Box>
          {isDroughtRiskStep && (
            <AccordionStyled
              expanded={droughtEventsExpanded}
              onChange={handleDroughtEventsChange}
              sx={{ borderBottom: "0px" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" gap="5px" alignItems="center" height="38px">
                  <Typography variant="subtitle1" fontWeight={700}>
                    Drought events
                  </Typography>
                  <Tooltip
                    title={
                      "We use datasets to represent drought impacts. These datasets can on the time series plots be overlaid with climate data and drought indices."
                    }
                  >
                    <Info sx={{ color: "#6C6C89" }} fontSize="small" />
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <AnomalieFilters
                  title={"Drought events from EM-DAT"}
                  toggleKey={"droughtEvents"}
                  isChecked={droughtEvents}
                  disabled={ipcScore}
                  openDialog={(e: any) =>
                    handleOpenDialog(e, "droughtEventsEMDAT")
                  }
                />
                <Box paddingTop="20px">
                  <AnomalieFilters
                    title={"IPC Score"}
                    toggleKey={"ipcScore"}
                    isChecked={ipcScore}
                    disabled={droughtEvents}
                    openDialog={(e: any) => handleOpenDialog(e, "IPCScore")}
                  />
                </Box>
              </AccordionDetails>
            </AccordionStyled>
          )}
        </Box>
      )}
      {(isDroughtRiskStep || isFinancialIndicesStep) &&
        geoUnit &&
        geoUnit?.features.length > 0 && (
          <GeographicalUnitPopUp
            open={open}
            setOpen={setOpen}
            hoveredArea={hoveredArea}
            setHoveredArea={setHoveredArea}
          />
        )}
      <MaskingDialog
        open={isOpenLandCoverMaskedDialog}
        setOpen={setIsOpenLandCoverMaskedDialog}
      />
    </Box>
  );
};

export default FiltersSidebar;

const AccordionStyled = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  width: "100%",
  backgroundColor: "white",
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
  position: "relative",
}));
